import { Box, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure, useIntersection } from '@mantine/hooks';
import { Info } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import InformationModal from '@/components/InformationModal';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import { useHostContext } from '@/context/HostContext.tsx';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { CREATOR_VIDEO_MAP_KEYS } from '@/utils/creatorVideoKeys';

const HowDoItWorkVideo: FC = () => {
	const [isOpenModal, { open, close }] = useDisclosure(false);
	const [isAutoplayVideo, setAutoplayVideo] = useState(false);
	const introVideo = partnerMetadata.getCreatorVideoSource(
		CREATOR_VIDEO_MAP_KEYS.INTRO
	);
	const { localization } = useHostContext();
	const { ref: targetRef, entry } = useIntersection({
		root: null,
		threshold: 0.5
	});

	useEffect(() => {
		setAutoplayVideo(entry?.isIntersecting ?? false);
	}, [entry]);

	if (!introVideo) {
		return null;
	}

	return (
		<>
			<div
				className="flex flex-col w-full items-center
			justify-center gap-[40px]"
				ref={targetRef}
			>
				<Text
					className="text-center flex align-center gap-[8px] [font-family:var(--font-body)] text-[16px] not-italic font-medium
			leading-[23px] tracking-[-0.3px] [leading-trim:both] [text-edge:cap]"
				>
					{localization.translate('creator', 'how_does_it_work')}
					<UnstyledButton
						className="!ml-5px"
						onClick={open}
					>
						<Info />
					</UnstyledButton>
				</Text>
				<Box
					className="!max-h-[518px] w-full rounded-[8px]
				sm:rounded-[16px] mb-[18px] sm:!mb-[46px] bg-black"
				>
					<VideoPlayer
						autoPlay={isAutoplayVideo}
						source={introVideo}
					/>
				</Box>
			</div>
			<InformationModal
				closeModal={close}
				isOpenedModal={isOpenModal}
				title={localization.translate('creator', 'how_does_it_work')}
			>
				<p className="mb-4">
					{localization.translate(
						'creator',
						'trainingSourceSelection.information_modal.intro'
					)}
				</p>

				<p className="mb-2">
					{localization.rich(
						'creator',
						'trainingSourceSelection.information_modal.who',
						{
							strong: (chunk) => <strong>{chunk}</strong>,
							br: () => <br />
						}
					)}
				</p>

				<p className="mb-2">
					{localization.rich(
						'creator',
						'trainingSourceSelection.information_modal.why_here',
						{
							strong: (chunk) => <strong>{chunk}</strong>,
							br: () => <br />
						}
					)}
				</p>

				<p className="mb-2">
					{localization.rich(
						'creator',
						'trainingSourceSelection.information_modal.why',
						{
							strong: (chunk) => <strong>{chunk}</strong>,
							br: () => <br />
						}
					)}
				</p>

				<p className="mb-2">
					{localization.rich(
						'creator',
						'trainingSourceSelection.information_modal.capabilities',
						{
							strong: (chunk) => <strong>{chunk}</strong>,
							br: () => <br />
						}
					)}
				</p>

				<p className="mb-4">
					<strong>
						{localization.translate(
							'creator',
							'trainingSourceSelection.information_modal.next_intro'
						)}
					</strong>
				</p>

				<ol className="list-decimal list-inside mb-4 space-y-2">
					<li>
						{localization.rich(
							'creator',
							'trainingSourceSelection.information_modal.steps.step1',
							{
								strong: (chunk) => <strong>{chunk}</strong>
							}
						)}
					</li>
					<li>
						{localization.translate(
							'creator',
							'trainingSourceSelection.information_modal.steps.step2'
						)}
					</li>
					<li>
						{localization.rich(
							'creator',
							'trainingSourceSelection.information_modal.steps.step3',
							{
								strong: (chunk) => <strong>{chunk}</strong>
							}
						)}
					</li>
					<li>
						{localization.translate(
							'creator',
							'trainingSourceSelection.information_modal.steps.step4'
						)}
					</li>
				</ol>

				<p className="mb-2">
					<span className="block">
						{localization.translate(
							'creator',
							'trainingSourceSelection.information_modal.promo.line1'
						)}
					</span>
					<span className="block">
						{localization.translate(
							'creator',
							'trainingSourceSelection.information_modal.promo.line2'
						)}
					</span>
				</p>

				<p className="mt-4">
					{localization.rich(
						'creator',
						'trainingSourceSelection.information_modal.cta',
						{
							strong: (chunk) => <strong>{chunk}</strong>
						}
					)}
				</p>

				<p className="mt-4">
					{localization.translate(
						'creator',
						'trainingSourceSelection.information_modal.closing'
					)}
				</p>
			</InformationModal>
		</>
	);
};

export default HowDoItWorkVideo;
